import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  formatStatementAmount,
  formatStatementDate,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.format'
import type { StatementPayment } from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementPayments/StatementPayments.style'

interface StatementPaymentsProps {
  nextPaymentAmount: Money
  /** ISO-8601 date. */
  nextPaymentDueOn: string
  payments: StatementPayment[]
}

/**
 * Payments section of the card-detail Statements tab (EX-40600).
 *
 * Strictly read-only, and that is a design guardrail rather than a gap: Extend
 * has no bank-payment capability, so there is deliberately NO pay / pay-off
 * action anywhere in this section. It shows the next amount due and the prior
 * payments, nothing more. Do not add a Pay control here without the capability
 * behind it.
 */
const StatementPayments: React.FC<StatementPaymentsProps> = ({
  nextPaymentAmount,
  nextPaymentDueOn,
  payments,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  return (
    <section data-testid="statementPayments">
      <div css={style.nextPayment}>
        <div>
          <p css={style.nextPaymentLabel}>{translate('Next payment due')}</p>
          <p css={style.nextPaymentFigure}>
            <span css={style.nextPaymentAmount}>
              {formatStatementAmount(formatCurrency, nextPaymentAmount)}
            </span>
            <span css={style.nextPaymentDue}>
              {translate('due ${dueDate}', {
                dueDate: formatStatementDate(nextPaymentDueOn),
              })}
            </span>
          </p>
        </div>
      </div>

      <h3 css={style.historyHeading}>{translate('Payment history')}</h3>
      <ul css={style.historyList}>
        {payments.map((payment) => (
          <li key={payment.id} css={style.historyRow}>
            <div>
              <div css={style.historyAmount}>
                {formatStatementAmount(formatCurrency, payment.amount)}
              </div>
              <div css={style.historyDetail}>
                {translate('Paid ${paidOn} · ${method}', {
                  paidOn: formatStatementDate(payment.paidOn),
                  method: payment.method,
                })}
              </div>
            </div>
            {/* Status is spelled out, never colour-only. */}
            <Pill color="saas" size="small">
              {translate('Paid')}
            </Pill>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default StatementPayments
